import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AIBlogGeneratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerated: (post: any) => void;
}

export function AIBlogGenerator({ open, onOpenChange, onGenerated }: AIBlogGeneratorProps) {
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error("Por favor, insira um tópico");
      return;
    }

    setLoading(true);

    try {
      console.log("Calling edge function: generate-blog-post", {
        topic: topic.substring(0, 50),
        hasKeywords: !!keywords,
      });

      const { data: generatedPost, error } = await supabase.functions.invoke(
        "generate-blog-post",
        {
          body: { topic, keywords },
        }
      );

      if (error) {
        console.error("Edge function error:", error);
        
        // Tratamento específico para diferentes tipos de erro
        if (error.message?.includes("404") || error.message?.includes("not found")) {
          throw new Error(
            "Função não encontrada. A edge function 'generate-blog-post' precisa ser deployada no Supabase. " +
            "Execute: supabase functions deploy generate-blog-post"
          );
        }
        
        if (error.message?.includes("401") || error.message?.includes("Unauthorized")) {
          throw new Error("Não autorizado. Por favor, faça login novamente.");
        }

        throw new Error(error.message || "Erro ao gerar post");
      }

      if (!generatedPost) {
        throw new Error("Nenhum dado retornado da função");
      }

      console.log("Generated post received:", {
        hasTitle: !!generatedPost.title,
        hasAuthorName: !!generatedPost.author_name,
        hasAuthorAvatar: !!generatedPost.author_avatar_url,
        hasContent: !!generatedPost.content,
      });

      toast.success("Post gerado com sucesso!");
      onGenerated(generatedPost);
      setTopic("");
      setKeywords("");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro desconhecido";
      console.error("Error generating blog post:", error);
      toast.error("Erro ao gerar post: " + message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Gerar Post com IA
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              Nossa IA irá criar um post de blog completo e otimizado para SEO sobre o tópico que você
              escolher. O conteúdo gerado incluirá título, resumo, conteúdo em Markdown, categoria e tags.
            </p>
          </div>

          <div>
            <Label htmlFor="topic">Sobre o que você quer escrever? *</Label>
            <Textarea
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Ex: Os benefícios da telemedicina para pacientes em áreas remotas"
              rows={4}
              disabled={loading}
            />
          </div>

          <div>
            <Label htmlFor="keywords">Palavras-chave (opcional)</Label>
            <Input
              id="keywords"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="Ex: telemedicina, saúde digital, acesso remoto"
              disabled={loading}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button onClick={handleGenerate} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Gerar Post
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
